import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

import { QuotationIcon } from '../components/Icon';
import LinkButton from '../components/LinkButton';

const ButtonMarkdownExpression = /\[(.*?)\]\((.*?)\){: \.btn}/;
const ImgMarkdownExpression = /!\[(.*?)\]\((\S*?)\s?("(.*?)?")?\)/;
const linkMarkdownExpression =
  /\[([\w\s\d@.]+)\]\(((?:\/|https?:\/\/|mailto:)[\S/?=#]+)\)/g;

function renderBlogContent(element, key, parentTag) {
  // # Handle leaf cases
  if (element.value === '\n') {
    return null;
  }
  if (element.type === 'text') {
    return element.value;
  }

  if (element.children) {
    var renderedChildren = [];
    element.children.forEach(function (child, i) {
      const childRet = renderBlogContent(child, key + '.' + i, element.tagName);
      renderedChildren.push(childRet);
    });
  }

  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(element.tagName)) {
    const HeaderTag = `${element.tagName}`;
    return (
      <HeaderTag className="xl:px-24 mt-12 mb-8">{renderedChildren}</HeaderTag>
    );
  }
  if (element.tagName === 'em') {
    return <em>{renderedChildren}</em>;
  }
  if (element.tagName === 'strong') {
    return <strong>{renderedChildren}</strong>;
  }
  if (element.tagName === 'a') {
    return (
      <a
        className="text-primary hover:text-primaryHover focus:text-primaryPressed"
        href={element.properties.href}
      >
        {renderedChildren}
      </a>
    );
  }
  if (element.tagName === 'code') {
    return <code>{renderedChildren}</code>;
  }
  if (element.tagName === 'pre') {
    return <pre className="mb-6">{renderedChildren}</pre>;
  }
  if (element.tagName === 'span') {
    return <span>{renderedChildren}</span>;
  }
  if (element.tagName === 'li') {
    return <li>{renderedChildren}</li>;
  }
  if (element.tagName === 'ul') {
    if (parentTag === 'li') {
      return (
        <ul className="list-disc px-6 xl:px-8 mb-6">{renderedChildren}</ul>
      );
    } else {
      return (
        <ul className="list-disc px-12 xl:px-36 mb-6">{renderedChildren}</ul>
      );
    }
  }
  if (element.tagName === 'ol') {
    if (parentTag === 'li') {
      return (
        <ol className="list-decimal px-6 xl:px-8 mb-6">{renderedChildren}</ol>
      );
    } else {
      return (
        <ol className="list-decimal px-12 xl:px-36 mb-6">{renderedChildren}</ol>
      );
    }
  }
  if (element.tagName === 'blockquote') {
    const innerElement = renderedChildren.filter((child) => {
      return child && child.type === 'p';
    })[0];
    if (innerElement && innerElement.props) {
      return (
        <>
          <QuotationIcon className="h-14 w-14 mt-12 mb-4 text-primary" />
          <blockquote className="text-3xl mb-12">
            {innerElement.props.children[0]}
          </blockquote>
        </>
      );
    } else {
      return null;
    }
  }
  if (element.tagName === 'img') {
    return (
      <img
        className="my-12"
        src={element.properties.src}
        alt={element.properties.alt}
      />
    );
  }

  // Unwrap Images
  if (element.tagName === 'p') {
    const containsImages = renderedChildren.filter((child) => {
      return child && child.type === 'img';
    });
    if (containsImages.length >= 1) {
      return <>{renderedChildren}</>;
    } else {
      return <p className="xl:px-24 mb-6">{renderedChildren}</p>;
    }
  }

  // Fallback case
  console.warn(
    `Unable to correctly render tag ${element.tagName} in renderBlogContent()`
  );
  const TagName = `${element.tagName}`;
  return <TagName>{renderedChildren}</TagName>;
}

const BlogContent = ({ content, className = '' }) => (
  <div className={`content ${className} text-base xl:px-36 2xl:px-64`}>
    {content.children.map((child, i) => {
      return renderBlogContent(child, i);
    })}
  </div>
);

BlogContent.propTypes = {
  content: PropTypes.object,
  className: PropTypes.string,
};

const MarkdownContent = ({ content }) => {
  if (!content) {
    return <></>;
  }

  let fragments = [];
  let ulBuff = [];
  let olBuff = [];

  const flushLists = () => {
    if (ulBuff.length) {
      fragments.push(
        <div
          key={ulBuff.join("")}
          className="mb-6"
          dangerouslySetInnerHTML={{ __html: `<ul class="list-disc pl-6">${ulBuff.join("\n")}</ul>` }}
        />
      );
      ulBuff = [];
    }
    if (olBuff.length) {
      fragments.push(
        <div
          key={olBuff.join("")}
          className="mb-6"
          dangerouslySetInnerHTML={{ __html: `<ol class="list-decimal pl-6">${olBuff.join("\n")}</ol>` }}
        />
      );
      olBuff = [];
    }
  };

  content
    .split(/\r?\n/)
    .filter((v) => v !== "")
    .forEach((line) => {
      let original = line;
      line = line.trim();

      // === Unordered lists ===
      if (/^[-*+]\s+(.+)/.test(line)) {
        line = line.replace(/^[-*+]\s+(.+)/, "<li>$1</li>");
        ulBuff.push(line);
        return;
      }

      // === Ordered lists ===
      if (/^\d+\.\s+(.+)/.test(line)) {
        line = line.replace(/^\d+\.\s+(.+)/, "<li>$1</li>");
        olBuff.push(line);
        return;
      }

      // Flush lists before non-list content
      flushLists();

      // === Buttons ===
      const buttonArgs = line.match(ButtonMarkdownExpression);
      if (buttonArgs) {
        fragments.push(
          <div key={line} className="mb-6">
            <p>
              <LinkButton text={buttonArgs[1]} to={buttonArgs[2]} />
            </p>
          </div>
        );
        return;
      }

      // === Images ===
      const imgArgs = line.match(ImgMarkdownExpression);
      if (imgArgs) {
        fragments.push(
          <div key={line} className="mb-6">
            <img
              src={imgArgs[2]}
              alt={imgArgs[1]}
              title={imgArgs[4]}
              className="max-w-full"
            />
          </div>
        );
        return;
      }

      // === Headers ===
      line = line.replace(/^######\s+(.+)/, "<h6>$1</h6>");
      line = line.replace(/^#####\s+(.+)/, "<h5>$1</h5>");
      line = line.replace(/^####\s+(.+)/, "<h4>$1</h4>");
      line = line.replace(/^###\s+(.+)/, "<h3>$1</h3>");
      line = line.replace(/^##\s+(.+)/, "<h2>$1</h2>");
      line = line.replace(/^#\s+(.+)/, "<h1>$1</h1>");

      // === Bold & italic ===
      line = line.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
      line = line.replace(/\*(.*?)\*/g, "<i>$1</i>");

      // === Links ===
      line = line.replace(linkMarkdownExpression, "<a href='$2'>$1</a>");

      // === Blockquotes ===
      if (/^>\s+/.test(original)) {
        line = line.replace(/^>\s+/, "");
        fragments.push(
          <blockquote key={original} className="pb-6 border-l-4 pl-4 italic">
            <QuotationIcon className="h-14 w-14 mb-4" />
            <div
              dangerouslySetInnerHTML={{ __html: line }}
              className="mb-6"
            />
          </blockquote>
        );
        return;
      }

      // === Default paragraph ===
      fragments.push(
        <div
          key={original}
          className="mb-6"
          dangerouslySetInnerHTML={{ __html: `<p>${line}</p>` }}
        />
      );
    });

  // Flush trailing lists
  flushLists();

  return <div className="markdown-content">{fragments}</div>;
};

BlogContent.propTypes = {
  content: PropTypes.string,
};

const ReactContent = ({ content, className }) => (
  <div className={className}>{content}</div>
);

ReactContent.propTypes = {
  content: PropTypes.node,
  className: PropTypes.string,
};

export {
  BlogContent,
  MarkdownContent,
  ReactContent,
  ButtonMarkdownExpression,
  ImgMarkdownExpression,
};
